import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getLeitnerBoxLabel } from '../../../config/levelConfig.js';

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function startOfMonth(ts) {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDate(ts) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildBoxMap(wordMastery = {}) {
  const boxes = { 1: [], 2: [], 3: [], 4: [], 5: [] };
  for (const [word, entry] of Object.entries(wordMastery)) {
    const box = Math.max(1, Math.min(5, entry?.box || 1));
    boxes[box].push({ word, ...entry });
  }
  for (const key of Object.keys(boxes)) {
    boxes[key].sort((a, b) => a.word.localeCompare(b.word));
  }
  return boxes;
}

function buildWeeklyMastered(box5Words = []) {
  const nowDay = startOfDay(Date.now());
  const buckets = [3, 2, 1, 0].map((offset) => {
    const start = nowDay - offset * 7 * 86400000;
    const end = start + 7 * 86400000;
    const count = box5Words.filter((w) => {
      const seen = w.lastSeen || 0;
      return seen >= start && seen < end;
    }).length;
    return { label: offset === 0 ? 'This week' : `${offset}w ago`, count };
  });
  return buckets;
}

export function WordsTab({ profile, onStartSession }) {
  const [expanded, setExpanded] = useState({});
  const wordMastery = profile?.tracker?.wordMastery || {};
  const boxes = useMemo(() => buildBoxMap(wordMastery), [wordMastery]);
  const totalWords = Object.keys(wordMastery).length;
  const box5Words = boxes[5] || [];
  const masteredTotal = box5Words.length;
  const monthStart = startOfMonth(Date.now());
  const masteredThisMonth = box5Words.filter((w) => (w.lastSeen || 0) >= monthStart).length;
  const weeklyMastered = useMemo(() => buildWeeklyMastered(box5Words), [box5Words]);
  const maxWeekly = Math.max(1, ...weeklyMastered.map((b) => b.count));
  const recentlyMastered = box5Words
    .slice()
    .sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0))
    .slice(0, 10);
  const hasData = totalWords > 0;
  const allEmpty = !hasData;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-5">
      <div className="mb-4 rounded-finley-xl border-2 border-finley-success/20 bg-finley-success/5 p-5 text-center shadow-finley-sm">
        <div className="font-finley-display text-5xl text-finley-success">{masteredTotal}</div>
        <div className="mt-1 font-finley-display text-xl text-finley-text">Words Mastered</div>
        <p className="mt-1 text-sm text-finley-text-light">
          {hasData ? `This Month: +${masteredThisMonth}` : "Complete sessions to start building word mastery!"}
        </p>
        {/* TODO: track explicit "moved-to-box-5" timestamps to replace lastSeen-based monthly proxy. */}
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-4 shadow-finley-sm">
        <div className="mb-3 font-finley-display text-xl text-finley-text">Leitner Boxes</div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((boxNumber) => {
            const meta = getLeitnerBoxLabel(boxNumber);
            const words = boxes[boxNumber] || [];
            const isOpen = !!expanded[boxNumber];
            const visibleWords = words.slice(0, 20);
            const hiddenCount = Math.max(0, words.length - visibleWords.length);
            return (
              <div key={boxNumber} className="rounded-finley-lg border border-finley-text-muted/15 bg-finley-bg shadow-finley-sm">
                <button
                  onClick={() => setExpanded((prev) => ({ ...prev, [boxNumber]: !prev[boxNumber] }))}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  style={{ borderLeft: `4px solid ${meta.color}` }}
                >
                  <div className="min-w-0">
                    <div className="font-finley-display text-base text-finley-text">{meta.label}</div>
                    <div className="text-xs text-finley-text-light">Box {boxNumber}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-finley-text">{words.length} words</span>
                    <span className="text-finley-text-light">{isOpen ? '▾' : '▸'}</span>
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="flex flex-wrap gap-2 px-4 pb-3">
                        {visibleWords.map((entry, idx) => (
                          <motion.span
                            key={entry.word}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="rounded-finley-full px-3 py-1.5 text-sm"
                            style={{ background: `${meta.color}22`, color: meta.color }}
                          >
                            {entry.word}
                          </motion.span>
                        ))}
                        {hiddenCount > 0 && <span className="rounded-finley-full bg-finley-bg-card px-3 py-1.5 text-sm text-finley-text-light">+{hiddenCount} more</span>}
                        {words.length === 0 && <span className="text-sm text-finley-text-light">No words yet</span>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-success/20 bg-finley-bg-card p-4 shadow-finley-sm">
        <div className="mb-3 font-finley-display text-xl text-finley-text">Words Over Time</div>
        <div className="space-y-2">
          {weeklyMastered.map((bar) => (
            <div key={bar.label}>
              <div className="mb-1 flex items-center justify-between text-xs text-finley-text-light">
                <span>{bar.label}</span>
                <span>{bar.count}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-finley-full bg-finley-bg">
                <div className="h-full rounded-finley-full bg-finley-success" style={{ width: `${(bar.count / maxWeekly) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
        {/* TODO: replace lastSeen-based weekly proxy with actual box-5 promotion events when available. */}
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-4 shadow-finley-sm">
        <div className="mb-3 font-finley-display text-xl text-finley-text">Recently Mastered</div>
        {recentlyMastered.length > 0 ? (
          <div className="space-y-2">
            {recentlyMastered.map((entry) => (
              <div key={entry.word} className="flex items-center justify-between rounded-finley-md border border-finley-text-muted/10 bg-finley-bg px-3 py-2">
                <span className="font-semibold text-finley-text">{entry.word}</span>
                <span className="text-xs text-finley-text-light">{entry.lastSeen ? `Mastered on ${formatDate(entry.lastSeen)}` : 'Mastered'}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="m-0 text-sm text-finley-text-light">No mastered words yet. Words move through Finley's review system as your child practices. Keep going!</p>
        )}
      </div>

      {allEmpty && (
        <div className="mb-4 rounded-finley-xl border-2 border-finley-primary/20 bg-finley-primary/5 p-4 text-center shadow-finley-sm">
          <p className="mb-3 text-sm text-finley-text-light">As your child plays, Finley will track every word they're learning. You'll see words progress from 'Just Learning' to 'Mastered' right here.</p>
          <button onClick={onStartSession} className="min-h-[44px] rounded-finley-lg bg-finley-primary px-4 py-2 text-sm font-semibold text-white shadow-finley-sm">Start a Session</button>
        </div>
      )}

      <div className="rounded-finley-lg border border-finley-text-muted/15 bg-finley-bg-card px-4 py-3 text-sm leading-6 text-finley-text-light">
        Finley uses spaced repetition to help words stick. New words start in "Just Learning" and move through the boxes as your child practices. Words in "Mastered" are reviewed less often because your child has proven they know them. If a word is forgotten, it moves back for more practice.
      </div>
    </motion.div>
  );
}
